import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import type { RootState } from "~/app/store";
import type { CustomDispatch } from "~/app/store/types";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { ConfigurationNameDialog } from "~/domains/configurator/components/dialogs";
import { saveConfiguratorAction } from "~/features/configurator/Linear/redux/actions";
import { addMessage } from "~/features/toasts/redux/actions";
import { useTrackLCSaveConfigurationClick } from "~/features/configurator/Linear/hooks/useTrackLCClicks";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { SAVED_CONFIGS_PAGE } from "~/utils/url/staticUrls";

export const useSaveConfigPopup = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch() as CustomDispatch<RootState>;
  const trackSave = useTrackLCSaveConfigurationClick();
  const { val: isModalVisible, setTrue: openModal, setFalse: closeModal } = useBooleanState(false);

  const submit = useCallback(
    async (name: string) => {
      const success = await dispatch(saveConfiguratorAction(name));
      if (!success) {
        dispatch(addMessage("error", t("page.linear.saveConfig.result.failure")));
        throw new Error("Saving the configuration failed.");
      }
      dispatch(addMessage("info", t("page.linear.saveConfig.result.success")));
      trackSave();
      redirectToUrl(SAVED_CONFIGS_PAGE);
    },
    [dispatch, t, trackSave],
  );

  return {
    openModal,
    component: isModalVisible ? (
      <ConfigurationNameDialog
        open
        title={t("page.linear.saveConfig.header")}
        label={t("page.linear.saveConfig.label")}
        requiredMessage={t("page.linear.saveConfig.helperLabel")}
        mandatoryText={t("editWishlistNamePopup.mandatory")}
        cancelLabel={t("cancel")}
        submitLabel={t("generic.save")}
        submitError={t("page.linear.saveConfig.result.failure")}
        onClose={closeModal}
        onSubmit={submit}
      />
    ) : null,
  };
};
